import React, { useState } from "react";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Boxes,
  CheckCircle2,
  ExternalLink,
  Image as ImageIcon,
  Layers,
  Loader2,
  Maximize2,
  Pencil,
  Printer,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import { EntityLink } from "@/components/shared/EntityLink";
import PrintDialog from "@/components/shared/PrintDialog";
import PrintLayoutTemplate from "@/components/tech/print/PrintLayoutTemplate";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { trpcClient } from "@/lib/trpc";
import { getFullUrl } from "@/lib/uploadFile";
import { formatDate, formatNumber, renderDetailValue } from "@/lib/utils";
import { PrintLayoutModalContainer } from "./PrintLayoutModalContainer";
import {
  extractMaterialSignature,
  extractRoutingSignature,
  resolveOrderCode,
} from "./prepress-imposition.helper";

export interface LayoutDetailPageProps {
  layoutId?: string;
  isZIndex?: boolean;
}

const FieldItem = ({
  label,
  value,
  mono,
  highlight,
}: {
  label: string;
  value?: React.ReactNode;
  mono?: boolean;
  highlight?: boolean;
}) => (
  <div className="space-y-0.5 min-w-0">
    <div className="text-[11px] font-medium text-slate-500">{label}</div>
    <div
      className={`text-sm font-semibold break-words ${mono ? "font-mono" : ""} ${highlight ? "text-indigo-700" : "text-slate-900"}`}
    >
      {value ?? renderDetailValue(value)}
    </div>
  </div>
);

export default function LayoutDetailPage({
  layoutId: propId,
  isZIndex = false,
}: LayoutDetailPageProps = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams({ strict: false }) as { layoutId?: string };
  const layoutId = propId || params.layoutId || "";
  const { hasPermission } = useRoleAccess();
  const canEdit =
    hasPermission("prepress", "update") ||
    hasPermission("orders", "update") ||
    hasPermission("production_orders", "update");
  const canDelete =
    hasPermission("prepress", "delete") ||
    hasPermission("production_orders", "delete");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [zoomImageOpen, setZoomImageOpen] = useState(false);

  const {
    data: layout,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["print-layout-detail", layoutId],
    queryFn: async () => {
      try {
        return await trpcClient.prepress.getPrintLayout.query({ id: layoutId });
      } catch (err: any) {
        // Fallback nếu bản ghi thuộc bảng master Layout cũ
        try {
          const legacy = await (trpcClient as any).technical.layouts.getById.query({
            id: layoutId,
          });
          if (legacy) {
            return {
              ...legacy,
              layoutCode: legacy.layoutId || legacy.code || legacy.id,
              layoutItems: [],
              isLegacyCatalog: true,
            };
          }
        } catch {
          // ignore fallback error and rethrow primary error
        }
        throw err;
      }
    },
    enabled: Boolean(layoutId),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if ((layout as any)?.isLegacyCatalog) {
        return await (trpcClient as any).technical.layouts.delete.mutate({ id: layoutId });
      }
      return await trpcClient.prepress.deletePrintLayout.mutate({ id: (layout as any).id });
    },
    onSuccess: async () => {
      toast.success("Đã xóa bài bình");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["print-layouts-workbench"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-list"] }),
        queryClient.invalidateQueries({ queryKey: ["prepress-hub-counts"] }),
      ]);
      navigate({ to: "/layouts", replace: true });
    },
    onError: (err: any) => {
      toast.error(err?.message || "Không thể xóa bài bình");
    },
  });

  if (isLoading) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Bình file", to: "/layouts" }]}
        title="Đang tải bài bình..."
      >
        <div className="flex items-center justify-center py-16 text-slate-500 text-sm gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          <span>Đang tải thông tin bài bình...</span>
        </div>
      </StandardDetailLayout>
    );
  }

  if (!layout) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Bình file", to: "/layouts" }]}
        entityLabel="Bài bình"
        backTo="/layouts"
        codeOrId={layoutId}
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  const d = layout as any;
  const items: any[] = Array.isArray(d.layoutItems) ? d.layoutItems : [];
  // Cùng quy tắc với API: lệnh đã huỷ / đã xoá không khoá bài bình (sửa, lên lệnh mới, xoá được)
  const hasPo = Boolean(d.productionOrder && !d.productionOrder.isDeleted && d.productionOrder.status !== "CANCELLED");
  const totalUps =
    items.length > 0
      ? items.reduce((s, li) => s + Math.max(1, Number(li.ups || 1)), 0)
      : Number(d.ups || 1);
  const sheetsPerUp = Number(d.sheetsPerUp || d.totalPrintSheets || 0);
  const totalPlannedQty = items.reduce(
    (s, li) => s + Math.max(0, Number(li.plannedQty || 0)),
    0,
  );
  const proofUrl = d.fileUrl ? getFullUrl(d.fileUrl) : "";
  const firstOd = items[0]?.orderDetail || d.orderDetails?.[0] || null;
  const orderCode = resolveOrderCode(firstOd);
  const orderId = firstOd?.orderId || firstOd?.order?.id || null;
  const customerName = firstOd?.order?.customer?.name || "—";

  return (
    <>
      <StandardDetailLayout
        breadcrumbs={[{ label: "Bình file", to: "/layouts" }]}
        title={d.layoutCode || d.layoutId || layoutId}
        entityId={d.id}
        entityType="print_layout"
        status={d.status ? <StatusBadge type="PrepressStatus" value={d.status} /> : undefined}
        timestamps={{ created: d.createdAt, updated: d.updatedAt }}
        showContextPanel={!isZIndex}
        onBack={() => navigate({ to: "/layouts" })}
        actions={
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPrintOpen(true)}
              className="h-8 text-xs"
            >
              <Printer className="h-3.5 w-3.5 mr-1" /> In
            </Button>

            {!hasPo && !d.isLegacyCatalog && canEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditModalOpen(true)}
                  className="h-8 text-xs"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Sửa
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate({
                      to: "/production-orders/create",
                      search: { layoutId: d.id } as any,
                    })
                  }
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  <Boxes className="h-3.5 w-3.5 mr-1.5" /> Tạo Lệnh SX
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </>
            )}

            {!hasPo && canDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
                className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Xóa
              </Button>
            )}
          </div>
        }
      >
        <div className="space-y-4">
          {/* Top Overview Grid: General Info + Proof Image */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-bold text-slate-900">
                    Thông tin chung bài bình
                  </h3>
                </div>
                <div className="flex items-center gap-1.5">
                  {items.length > 1 ? (
                    <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-[11px] font-bold">
                      Bài ghép ({items.length} SP)
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[11px]">
                      Bài đơn
                    </Badge>
                  )}
                  {hasPo ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[11px] font-bold">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Đã tạo lệnh
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-800 border-amber-200 text-[11px] font-semibold">
                      Chờ tạo lệnh
                    </Badge>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <FieldItem label="Mã bài bình" value={d.layoutCode || d.layoutId} mono highlight />
                <FieldItem label="Khổ giấy in" value={d.sheetSize || "—"} mono />
                <FieldItem label="Tổng Số Ups" value={`${formatNumber(totalUps)} ups`} mono highlight />
                <FieldItem
                  label="SL tờ in mỗi Ups"
                  value={`${formatNumber(sheetsPerUp)} tờ`}
                  mono
                  highlight
                />
                <FieldItem
                  label="Đơn hàng"
                  value={
                    orderId && orderCode ? (
                      <EntityLink entityType="order" id={orderId} code={orderCode} />
                    ) : (
                      orderCode || "—"
                    )
                  }
                  mono
                />
                <FieldItem
                  label="Khách hàng"
                  value={
                    firstOd?.order?.customer?.id || firstOd?.order?.customerId ? (
                      <EntityLink
                        entityType="customer"
                        id={firstOd?.order?.customer?.id || firstOd?.order?.customerId}
                        code={firstOd?.order?.customer?.code}
                        label={customerName}
                      />
                    ) : (
                      customerName
                    )
                  }
                />
                <FieldItem
                  label="Người bình file"
                  value={d.creator?.fullName || "—"}
                />
                <FieldItem
                  label="Lệnh sản xuất"
                  value={
                    hasPo ? (
                      <EntityLink
                        entityType="productionOrder"
                        id={d.productionOrder.id}
                        code={d.productionOrder.orderId}
                      />
                    ) : (
                      "Chưa tạo lệnh"
                    )
                  }
                  mono
                />
              </div>

              {(d.machine || d.dieCut || d.notes || d.createdAt) && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 border-t border-slate-100 pt-3">
                  {d.machine && (
                    <FieldItem label="Máy in dự kiến" value={d.machine.name || d.machine.machineId} />
                  )}
                  {d.dieCut && (
                    <FieldItem label="Khuôn bế" value={d.dieCut.name || d.dieCut.dieCutId} />
                  )}
                  {d.createdAt && (
                    <FieldItem label="Ngày bình file" value={formatDate(d.createdAt)} mono />
                  )}
                  {d.notes && (
                    <div className="sm:col-span-3">
                      <FieldItem label="Ghi chú bài bình" value={d.notes} />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Proof Image Card */}
            <div className="lg:col-span-4 rounded-xl border border-slate-200 bg-white p-4 shadow-2xs flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2 mb-3">
                <span className="text-sm font-bold text-slate-900">Ảnh sơ đồ bài bình</span>
                {proofUrl && (
                  <a
                    href={proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium text-indigo-600 hover:underline inline-flex items-center gap-1"
                  >
                    Mở gốc <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              {proofUrl ? (
                <div
                  onClick={() => setZoomImageOpen(true)}
                  className="relative group flex-1 min-h-[180px] rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden cursor-pointer"
                >
                  <img
                    src={proofUrl}
                    alt={d.layoutCode || "Ảnh bài bình"}
                    className="max-h-52 max-w-full object-contain"
                  />
                  <div className="absolute inset-0 bg-slate-900/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-semibold gap-1.5">
                    <Maximize2 className="h-4 w-4" /> Nhấn để phóng to
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-h-[160px] rounded-lg border border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400 text-xs gap-1.5">
                  <ImageIcon className="h-6 w-6" />
                  <span>Chưa đính kèm ảnh bài bình</span>
                </div>
              )}
            </div>
          </div>

          {/* Detail Items Breakdown Table */}
          <div className="rounded-xl border border-slate-200 bg-white shadow-2xs overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-900">
                Chi tiết sản phẩm trong bài bình ({items.length})
              </h3>
              <span className="text-xs font-semibold text-emerald-700 font-mono">
                Tổng SL SX: {formatNumber(totalPlannedQty)} SP
              </span>
            </div>

            <div role="region" aria-label="Chi tiết sản phẩm bài bình" tabIndex={0} className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase text-slate-600">
                    <th className="text-left py-2.5 px-4">Mã ĐH</th>
                    <th className="text-left py-2.5 px-3">Sản phẩm</th>
                    <th className="text-left py-2.5 px-3">Chất liệu & Quy trình SX</th>
                    <th className="text-right py-2.5 px-3">SL Đặt</th>
                    <th className="text-center py-2.5 px-3">Số Ups</th>
                    <th className="text-right py-2.5 px-3">SL Tờ in / Ups</th>
                    <th className="text-right py-2.5 px-4">SL SX từng loại</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-400">
                        Không có chi tiết đơn hàng đính kèm.
                      </td>
                    </tr>
                  ) : (
                    items.map((li: any) => {
                      const od = li.orderDetail;
                      const ordCode = resolveOrderCode(od);
                      const ordId = od?.orderId || od?.order?.id;
                      const mat = extractMaterialSignature(od);
                      const route = extractRoutingSignature(od);
                      return (
                        <tr key={li.id} className="hover:bg-slate-50/70">
                          <td className="py-2.5 px-4 font-mono font-bold text-indigo-700">
                            {ordId && ordCode ? (
                              <EntityLink entityType="order" id={ordId} code={ordCode} />
                            ) : (
                              ordCode || "—"
                            )}
                          </td>
                          <td className="py-2.5 px-3 font-semibold text-slate-900">
                            {od?.productName || od?.product?.productName || "—"}
                            {/* Mã sản phẩm để phân biệt sản phẩm cùng tên (FB-1GS4DJ) */}
                            {(od?.productCode || od?.product?.productCode) && (
                              <div className="font-mono text-[11px] font-semibold text-slate-600" data-testid={`layout-item-code-${li.id}`}>
                                {od?.productCode || od?.product?.productCode}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-slate-600">
                            <span className="font-medium text-slate-800">{mat.label}</span>
                            <span className="text-slate-400"> • </span>
                            <span>{route.label}</span>
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-slate-700">
                            {formatNumber(od?.quantity || 0)}
                          </td>
                          <td className="py-2.5 px-3 text-center font-mono font-bold text-indigo-700">
                            {formatNumber(li.ups || 1)} ups
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-slate-800">
                            {formatNumber(li.sheetsPerUp || sheetsPerUp)}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-700">
                            {formatNumber(li.plannedQty || 0)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </StandardDetailLayout>

      {/* Edit Modal (Rule 2 Canonical Form) */}
      <PrintLayoutModalContainer
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        mode="edit"
        layoutId={d.id}
        onSuccess={async () => {
          await refetch();
        }}
      />

      {/* Print Preview */}
      {printOpen && (
        <PrintDialog
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          type="custom"
          title={`In Sơ đồ Bài bình - ${d.layoutCode || d.layoutId}`}
        >
          <PrintLayoutTemplate data={{ ...d, layoutId: d.layoutCode || d.layoutId }} />
        </PrintDialog>
      )}

      {/* Proof Image Zoom */}
      <Dialog open={zoomImageOpen} onOpenChange={setZoomImageOpen}>
        <DialogContent className="max-w-5xl p-3">
          <DialogHeader>
            <DialogTitle>
              Sơ đồ bài bình {d.layoutCode || d.layoutId}
            </DialogTitle>
          </DialogHeader>
          {proofUrl && (
            <div className="flex items-center justify-center bg-slate-50 rounded-lg p-2 max-h-[82vh] overflow-auto">
              <img
                src={proofUrl}
                alt={d.layoutCode || "Sơ đồ bài bình"}
                className="max-h-[78vh] max-w-full object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Xóa bài bình {d.layoutCode || d.layoutId}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Các sản phẩm thuộc bài bình này sẽ được hoàn lại số lượng về danh sách chờ bình file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              Xóa bài bình
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
